import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const api = {
  // Auth
  me: () => client.get("/auth/me").then((r) => r.data),
  exchangeSession: (session_id, credential) =>
    client
      .post("/auth/session", { session_id, credential })
      .then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),

  // Data
  members: () => client.get("/members").then((r) => r.data),
  transactions: () => client.get("/transactions").then((r) => r.data),
  portfolioSummary: () => client.get("/portfolio/summary").then((r) => r.data),
  navHistory: () => client.get("/portfolio/nav-history").then((r) => r.data),

  // Daily NAV snapshots (admin-managed)
  listDailyNav: () => client.get("/daily-nav").then((r) => r.data),
  upsertDailyNav: (body) =>
    client.post("/admin/daily-nav", body).then((r) => r.data),
  deleteDailyNav: (dateISO) =>
    client.delete(`/admin/daily-nav/${encodeURIComponent(dateISO)}`).then((r) => r.data),

  // Admin
  addMember: (body) =>
    client.post("/admin/members", body).then((r) => r.data),
  editMember: (id, body) =>
    client.patch(`/admin/members/${id}`, body).then((r) => r.data),
  removeMember: (id) =>
    client.delete(`/admin/members/${id}`).then((r) => r.data),
  cleanupDuplicateMembers: () =>
    client.post("/admin/members/cleanup-duplicates").then((r) => r.data),
  listAuthorizedGmails: () =>
    client.get("/admin/authorized-gmails").then((r) => r.data),
  addAuthorizedGmail: (body) =>
    client.post("/admin/authorized-gmails", body).then((r) => r.data),
  editAuthorizedGmail: (email, body) =>
    client
      .patch(`/admin/authorized-gmails/${encodeURIComponent(email)}`, body)
      .then((r) => r.data),
  revokeAuthorizedGmail: (email) =>
    client
      .delete(`/admin/authorized-gmails/${encodeURIComponent(email)}`)
      .then((r) => r.data),
  addTransaction: (body) =>
    client.post("/admin/transactions", body).then((r) => r.data),
  bulkTransactions: (rows, replaceAll = false) =>
    client.post("/admin/transactions/bulk", { rows, replaceAll }).then((r) => r.data),
  bulkNavHistory: (rows, replaceDaily = false) =>
    client.post("/admin/nav-history/bulk", { rows, replaceDaily }).then((r) => r.data),
  editTransaction: (id, body) =>
    client.patch(`/admin/transactions/${id}`, body).then((r) => r.data),
  deleteTransaction: (id) =>
    client.delete(`/admin/transactions/${id}`).then((r) => r.data),
  updatePortfolioValues: (body) =>
    client.post("/admin/portfolio-values", body).then((r) => r.data),

  // Portfolio holdings
  holdings: () => client.get("/portfolio/holdings").then((r) => r.data),
  uploadHoldings: (body) =>
    client.post("/admin/portfolio/holdings/upload", body).then((r) => r.data),
  refreshHoldingsPrices: () =>
    client.post("/admin/portfolio/holdings/refresh-prices").then((r) => r.data),

  // Audit log
  auditLog: (limit = 200) =>
    client.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data),

  // Profit & Loss
  listPnl: () => client.get("/pnl").then((r) => r.data),
  getPnl: (fy) => client.get(`/pnl/${encodeURIComponent(fy)}`).then((r) => r.data),
  upsertPnl: (body) => client.post("/admin/pnl", body).then((r) => r.data),
  deletePnl: (fy) =>
    client.delete(`/admin/pnl/${encodeURIComponent(fy)}`).then((r) => r.data),

  // Derivative P&L
  listDerivativePnl: () => client.get("/derivative-pnl").then((r) => r.data),
  getDerivativePnl: (fy) =>
    client.get(`/derivative-pnl/${encodeURIComponent(fy)}`).then((r) => r.data),
  upsertDerivativePnl: (body) =>
    client.post("/admin/derivative-pnl", body).then((r) => r.data),
  deleteDerivativePnl: (fy) =>
    client.delete(`/admin/derivative-pnl/${encodeURIComponent(fy)}`).then((r) => r.data),

  // Bonus
  listBonus: () => client.get("/bonus").then((r) => r.data),
  getBonus: (fy) => client.get(`/bonus/${encodeURIComponent(fy)}`).then((r) => r.data),
  upsertBonus: (body) => client.post("/admin/bonus", body).then((r) => r.data),
  deleteBonus: (fy) =>
    client.delete(`/admin/bonus/${encodeURIComponent(fy)}`).then((r) => r.data),

  // Integrity / recovery
  integrityCheck: () =>
    client.get("/admin/integrity-check").then((r) => r.data),
  rebuildBalances: () =>
    client.post("/admin/rebuild-balances").then((r) => r.data),
  unitsSnapshot: (date) =>
    client.get(`/admin/units-snapshot`, { params: { date } }).then((r) => r.data),

  // Page-access settings
  getSettings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (body) =>
    client.post("/admin/settings", body).then((r) => r.data),

  // Ledger
  ledgerAccounts: () => client.get("/ledger/accounts").then((r) => r.data),
  ledgerEntries: (accountId) =>
    client
      .get("/ledger/entries", { params: accountId ? { accountId } : {} })
      .then((r) => r.data),
  ledgerReconciliation: () =>
    client.get("/ledger/reconciliation").then((r) => r.data),
  ledgerReconciliationDrilldown: (identity) =>
    client
      .get("/ledger/reconciliation/drilldown", { params: { identity } })
      .then((r) => r.data),
  createLedgerAccount: (body) =>
    client.post("/admin/ledger/accounts", body).then((r) => r.data),
  editLedgerAccount: (id, body) =>
    client.patch(`/admin/ledger/accounts/${id}`, body).then((r) => r.data),
  deleteLedgerAccount: (id) =>
    client.delete(`/admin/ledger/accounts/${id}`).then((r) => r.data),
  cleanupDuplicateLedgerAccounts: () =>
    client.post("/admin/ledger/accounts/cleanup-duplicates").then((r) => r.data),
  createLedgerEntry: (body) =>
    client.post("/admin/ledger/entries", body).then((r) => r.data),
  bulkLedgerEntries: (body) =>
    client.post("/admin/ledger/entries/bulk", body).then((r) => r.data),
  editLedgerEntry: (id, body) =>
    client.patch(`/admin/ledger/entries/${id}`, body).then((r) => r.data),
  createLedgerTransfer: (body) =>
    client.post("/admin/ledger/transfer", body).then((r) => r.data),
  createLedgerJournal: (body) =>
    client.post("/admin/ledger/journal", body).then((r) => r.data),
  backfillAutoJournals: () =>
    client.post("/admin/ledger/backfill-from-transactions").then((r) => r.data),
  deleteLedgerEntry: (id) =>
    client.delete(`/admin/ledger/entries/${id}`).then((r) => r.data),
};

export default client;
